// `config/` sits outside `src/` and has no alias, like root-level `lib/`.
import {
  getValueCheckPath as getValueCheckPathForLocale,
  getValueCheckPublicPath,
} from "../../../config/value-check-paths";
import { CANONICAL_HOST } from "@/config";
import { locales } from "@/i18n/config";

/**
 * WHO-6534, the value check ("Wat is jouw item waard?"): one photo, what we recognise, what
 * comparable pieces sold for on Whoppah, and one button into create-ad v2. No account needed
 * until "Create ad".
 */

/** Statsig gate: the page answers 404 while it is off (Thomas, 27 Oct: you and Evelien first). */
export const VALUE_CHECK_GATE = "value_check";

/** Turnstile action; the gateway's `@turnstile(action: "value_check")` checks it. */
export const VALUE_CHECK_TURNSTILE_ACTION = "value_check";

export const VALUE_CHECK_API_ROUTE = "/api/value-check";

/** The pricing engine takes photos up to about 4 MB; larger uploads are refused up front. */
export const VALUE_CHECK_MAX_PHOTO_BYTES = 4 * 1024 * 1024;

/** How many pieces for sale the result shows ("Nu te koop op Whoppah"). */
export const VALUE_CHECK_COMPARABLES = 6;

/**
 * How long the browser keeps a check for "Create ad" (Thomas, 27 Oct: in the browser, nothing on
 * our servers). After that the photo and what we saw are dropped.
 */
export const VALUE_CHECK_STASH_DAYS = 7;

/** `?from=value-check` on create-ad v2: pick up the stashed photo after login. */
export const VALUE_CHECK_FROM_PARAM = "from";
export const VALUE_CHECK_FROM_VALUE = "value-check";

/**
 * The page's path without the locale prefix (`Link` adds it): nl /waardecheck, de /wertcheck,
 * fr /estimation, it /valutazione, es /tasacion, en /value-check. The table lives in
 * `config/value-check-paths.js` because the rewrites and redirects need it too.
 */
export const getValueCheckPath = (locale: string | undefined): string =>
  getValueCheckPathForLocale(locale);

/**
 * The hreflang cluster for `HrefLang urlMap`: every routed locale's path without prefix, so
 * `/en-us` (English word, WHO-5766) is in it while it routes.
 */
export const getValueCheckHreflangUrls = (): Record<string, string> =>
  Object.fromEntries(
    locales.map(locale => [locale, getValueCheckPathForLocale(locale)])
  );

/** Absolute canonical URL of the page in a locale. */
export const getValueCheckCanonicalUrl = (locale: string | undefined): string =>
  `${CANONICAL_HOST}${getValueCheckPublicPath(locale)}`;
